import React from 'react';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../ui/Kit';
import { resolveDisplayName } from '../../lib/usersDirectory';
import { formatCallOutcomeLabel } from '../../lib/leadActivityFeed';

const { FiPhone } = FiIcons;

function formatCallDurationSeconds(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getCallOutcomeBadgeStyle(outcome) {
  if (outcome === 'booked_meeting') {
    return { backgroundColor: 'rgba(0, 170, 255, 0.12)', color: '#00AAFF' };
  }
  if (outcome === 'closed_business') {
    return { backgroundColor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' };
  }
  return null;
}

function CallOutcomeBadge({ outcome }) {
  const label = formatCallOutcomeLabel(outcome);
  const style = getCallOutcomeBadgeStyle(outcome);
  if (!style) {
    return <span className="capitalize">{label}</span>;
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      style={style}
    >
      {label}
    </span>
  );
}

export function NoCallsEmptyState({ title, message, hasPhone, onCallLead, children }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 py-12 px-4 text-center">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <SafeIcon icon={FiPhone} className="w-5 h-5 text-gray-400" />
      </div>
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="text-xs text-gray-400 mt-1 max-w-sm">{message}</p>
      <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
        {onCallLead && (
          <Button size="sm" onClick={onCallLead} disabled={!hasPhone}>
            <SafeIcon icon={FiPhone} className="mr-2" />
            Call Lead
          </Button>
        )}
        {children}
      </div>
    </div>
  );
}

export default function CallLogTable({ callLogs = [], hasPhone, onCallLead }) {
  if (!callLogs.length) {
    return (
      <NoCallsEmptyState
        title="No calls logged yet"
        message="Outbound and inbound calls to this lead will appear here once they are recorded."
        hasPhone={hasPhone}
        onCallLead={onCallLead}
      />
    );
  }

  return (
    <div className="overflow-x-auto flex-1">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-100/80 border-b border-gray-200 sticky top-0 z-[1]">
          <tr>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Date/Time
            </th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Agent
            </th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Direction
            </th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Outcome
            </th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Duration
            </th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[140px]">
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {callLogs.map((row) => (
            <tr key={row.id} className="bg-white border-b border-gray-100 hover:bg-gray-50/80 align-top">
              <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                {row.called_at
                  ? format(new Date(row.called_at), 'MMM d, yyyy h:mm a')
                  : '—'}
              </td>
              <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                {resolveDisplayName(row.agent_id || row.user_id, row.user) || '—'}
              </td>
              <td className="px-4 py-3 text-gray-700 capitalize whitespace-nowrap">
                {row.direction ? String(row.direction).replace(/_/g, ' ') : '—'}
              </td>
              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                <CallOutcomeBadge outcome={row.outcome} />
              </td>
              <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                {formatCallDurationSeconds(row.duration_seconds)}
              </td>
              <td className="px-4 py-3 text-gray-600 max-w-xs">
                {row.notes ? (
                  <span className="whitespace-pre-wrap break-words">{row.notes}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
